import api from '@/libs/axiosInstance';
import type { ApiResponse, ShareDriveFolderResult } from '@fluenceur/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

export interface ShareDriveInput {
  emails?: string[];
  domain?: string;
}

interface ShareDriveResponse {
  results: ShareDriveFolderResult[];
}

const shareDrive = async (input: ShareDriveInput): Promise<ShareDriveFolderResult[]> => {
  const response = await api.post<ApiResponse<ShareDriveResponse>>(
    '/dashboard/integrations/google-drive/permissions',
    input
  );

  if (response.data.success && response.data.data) {
    return response.data.data.results;
  }

  throw new Error(response.data.message || 'Failed to share Google Drive folder');
};

export const useShareDrive = () => {
  const queryClient = useQueryClient();

  return useMutation<ShareDriveFolderResult[], Error, ShareDriveInput>({
    mutationFn: shareDrive,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drive', 'permissions'] });
    },
  });
};

const revokeDriveAccess = async (permissionId: string): Promise<void> => {
  await api.delete(`/dashboard/integrations/google-drive/permissions/${permissionId}`);
};

export const useRevokeDriveAccess = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: revokeDriveAccess,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drive', 'permissions'] });
    },
  });
};
